import React, { useEffect, useRef, useState } from "react";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { message, Button, Modal } from "antd";

interface CVUploadProps {
  jobDescription: any;
  jobDescriptionText: string;
  currentJDBatches: any[];
  onCVInputChange: (files: File[]) => void;
  isScanning: boolean;
  descriptionInputType?: string;
}

export const CVUpload: React.FC<CVUploadProps> = ({
  jobDescription,
  jobDescriptionText,
  currentJDBatches,
  onCVInputChange,
  isScanning,
  descriptionInputType = "upload",
}) => {
  const cvInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isClearModalVisible, setIsClearModalVisible] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);

    // Validate file types
    const validFiles = newFiles.filter((file) => {
      const validTypes = [".pdf", ".doc", ".docx"];
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      const isValid = validTypes.includes(fileExtension);
      if (!isValid) {
        message.warning(
          `${file.name} is not a valid format. Only PDF, DOC, DOCX allowed.`,
        );
      }
      return isValid;
    });

    const validSizeFiles = validFiles.filter((file) => {
      const isValidSize = file.size > 0 && file.size <= 1048576;
      if (!isValidSize) {
        message.warning(`${file.name} exceeds 1MB limit and was not added.`);
      }
      return isValidSize;
    });

    if (selectedFiles.length + validSizeFiles.length > 50) {
      message.error(
        `Maximum 50 documents allowed. You can only add ${50 - selectedFiles.length} more.`,
      );
      const remainingSlots = 50 - selectedFiles.length;
      const filesToAdd = validSizeFiles.slice(0, remainingSlots);
      setSelectedFiles((prev) => [...prev, ...filesToAdd]);
      if (filesToAdd.length > 0) {
        message.success(
          `${filesToAdd.length} file(s) added. Total: ${selectedFiles.length + filesToAdd.length}/50`,
        );
      }
    } else {
      setSelectedFiles((prev) => [...prev, ...validSizeFiles]);
      if (validSizeFiles.length > 0) {
        message.success(
          `${validSizeFiles.length} file(s) added. Total: ${selectedFiles.length + validSizeFiles.length}/50`,
        );
      }
    }

    e.target.value = "";
  };

  const handleClearAll = () => {
    if (selectedFiles.length === 0) return;
    setIsClearModalVisible(true);
  };

  const confirmClearAll = () => {
    setSelectedFiles([]);
    message.success("All files cleared successfully");
    setIsClearModalVisible(false);
  };

  const handleCommenceScanning = () => {
    if (selectedFiles.length === 0) {
      message.warning("Please select at least one CV to scan.");
      return;
    }
    if (isScanning) return;
    onCVInputChange(selectedFiles);
  };

  const handleClick = () => {
    if (descriptionInputType === "upload") {
      if (!jobDescription) {
        message.error("Please upload a Job Description file first.");
        return;
      }
      if (!jobDescriptionText) {
        message.error("Job Description is still processing. Please wait...");
        return;
      }
    } else if (descriptionInputType === "manual") {
      if (!jobDescriptionText || !jobDescriptionText.trim()) {
        message.error("Please paste a Job Description text first.");
        return;
      }
    }

    if (currentJDBatches.length === 0) {
      cvInputRef.current?.click();
    }
  };

  // Check if JD is provided based on input type
  const isJDProvided = () => {
    if (descriptionInputType === "upload") {
      return !!jobDescription && !!jobDescriptionText;
    } else if (descriptionInputType === "manual") {
      return !!(jobDescriptionText && jobDescriptionText.trim());
    }
    return false;
  };

  const isJDReady = isJDProvided();

  useEffect(() => {
    if (!isScanning) {
      // Don't auto-clear, let user decide
    }
  }, [isScanning]);

  return (
    <div className="flex-[2]">
      <div className="border rounded-lg bg-white p-6 shadow-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Upload CVs</h3>
          {selectedFiles.length > 0 && (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={handleClearAll}
            >
              Clear All
            </Button>
          )}
        </div>

        <input
          type="file"
          ref={cvInputRef}
          multiple
          hidden
          accept=".pdf,.doc,.docx"
          onChange={handleFileSelect}
        />

        <div
          onClick={handleClick}
          className={`flex flex-col justify-center items-center border-2 border-dashed rounded-lg p-10 text-center transition ${
            !isJDReady || currentJDBatches.length > 0
              ? "cursor-not-allowed bg-gray-50 border-gray-300"
              : "cursor-pointer hover:bg-blue-50 border-blue-300"
          }`}
        >
          {selectedFiles.length === 0 ? (
            <UploadOutlined className="text-3xl mb-2 text-blue-500" />
          ) : (
            <div className="mb-2 inline-flex items-center justify-center bg-green-500 text-white rounded-full w-12 h-12">
              <span className="text-lg font-bold">{selectedFiles.length}</span>
            </div>
          )}

          <p className="text-gray-600 font-medium">
            {selectedFiles.length === 0
              ? "Click to upload Resumes"
              : `${selectedFiles.length} document(s) selected`}
          </p>

          <p className="text-xs text-gray-400 mt-1">
            {selectedFiles.length === 0
              ? "PDF, DOC, DOCX (Max 1MB each)"
              : `Click to add more from another folder`}
          </p>

          {!isJDReady && selectedFiles.length === 0 && (
            <p className="text-xs text-red-400 mt-2">
              {descriptionInputType === "upload"
                ? "⚠️ Please upload a Job Description file first"
                : "⚠️ Please paste a Job Description text first"}
            </p>
          )}
        </div>

        {selectedFiles.length === 0 && (
          <p className="mt-4 text-center text-sm text-gray-600 italic">
            ⚠️ A maximum of 50 documents may be uploaded, each not exceeding
            1MB.
          </p>
        )}

        {selectedFiles.length > 0 && selectedFiles.length < 50 && (
          <p className="mt-3 text-center text-xs text-blue-600">
            💡 You can click again to add more files from different folders
          </p>
        )}

        {selectedFiles.length >= 50 && (
          <p className="mt-4 text-center text-sm text-red-500 font-medium">
            ⚠️ Maximum limit of 50 documents reached.
          </p>
        )}

        <button
          onClick={handleCommenceScanning}
          disabled={isScanning || selectedFiles.length === 0 || !isJDReady}
          className={`mt-3 w-full px-4 py-2 rounded transition font-medium
            ${
              isScanning || selectedFiles.length === 0 || !isJDReady
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
        >
          {isScanning
            ? "Scanning..."
            : selectedFiles.length === 0
              ? "No files selected"
              : !isJDReady
                ? descriptionInputType === "upload"
                  ? "Upload JD first"
                  : "Paste JD first"
                : `Commence Scanning (${selectedFiles.length})`}
        </button>

        {/* Show status of JD */}
        {isJDReady && (
          <div className="mt-3 text-xs text-green-600 text-center">
            ✓ Job Description ready
          </div>
        )}
      </div>

      {/* Clear All Confirmation Modal */}
      <Modal
        title="Clear All Files"
        open={isClearModalVisible}
        onOk={confirmClearAll}
        onCancel={() => setIsClearModalVisible(false)}
        okText="Yes, Clear All"
        okType="danger"
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to clear all {selectedFiles.length} selected
          file(s)?
        </p>
      </Modal>
    </div>
  );
};
